import { useState } from 'react';
import { TrendingUp, TrendingDown, Check, X } from 'lucide-react';

const CONVICTION_STYLE = {
  HIGH:   'border-l-korinth-accent',
  MEDIUM: 'border-l-korinth-warning',
  LOW:    'border-l-korinth-muted',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export default function OpportunityCard({ signal, onAction }) {
  const [actionState, setActionState] = useState(signal.user_action || null);
  const [showReasoning, setShowReasoning] = useState(false);
  const [reasoning, setReasoning] = useState('');

  async function handleAction(action) {
    try {
      const res = await fetch(`/api/signals/${signal.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reasoning: reasoning || null }),
      });
      if (res.ok) {
        setActionState(action.toUpperCase());
        setShowReasoning(false);
        onAction?.(signal.id, action);
      }
    } catch { /* silent */ }
  }

  const isBullish = signal.direction === 'bullish';

  return (
    <div className={`bg-korinth-surface border border-korinth-border rounded-lg border-l-4 ${CONVICTION_STYLE[signal.conviction] || ''} ${actionState ? 'opacity-60' : ''}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isBullish ? <TrendingUp className="w-5 h-5 text-korinth-accent" /> : <TrendingDown className="w-5 h-5 text-korinth-danger" />}
            <span className="text-sm font-mono font-bold text-white">{signal.asset}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isBullish ? 'bg-korinth-accent/10 text-korinth-accent' : 'bg-korinth-danger/10 text-korinth-danger'}`}>
              {signal.direction}
            </span>
          </div>
          <div className="text-right">
            <div className="text-sm font-mono font-bold text-white">{signal.score}/10</div>
            <div className="text-[9px] text-korinth-muted">{signal.conviction}</div>
          </div>
        </div>

        {/* Sources */}
        <div className="flex flex-wrap gap-1 mb-3">
          {(signal.sources || []).map((src, i) => (
            <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-border text-korinth-text">{src}</span>
          ))}
        </div>

        {/* Time */}
        <p className="text-[10px] text-korinth-muted font-mono mb-3">{timeAgo(signal.created_at)}</p>

        {/* Action buttons */}
        {!actionState ? (
          <div className="space-y-2">
            {showReasoning && (
              <textarea
                placeholder="Why? (optional)"
                value={reasoning}
                onChange={e => setReasoning(e.target.value)}
                className="w-full px-3 py-1.5 bg-korinth-bg border border-korinth-border rounded text-xs font-mono text-white placeholder-korinth-muted/50 focus:border-korinth-accent outline-none h-12 resize-none"
              />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => showReasoning ? handleAction('acted') : setShowReasoning(true)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-mono font-bold bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20"
              >
                <Check className="w-3 h-3" /> I Acted
              </button>
              <button
                onClick={() => showReasoning ? handleAction('skipped') : setShowReasoning(true)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-mono font-bold bg-korinth-muted/10 text-korinth-muted border border-korinth-border hover:bg-korinth-muted/20"
              >
                <X className="w-3 h-3" /> Skipped
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center text-[10px] font-mono text-korinth-muted py-1">
            {actionState === 'ACTED' ? '✓ Acted on this signal' : '— Skipped'}
          </div>
        )}
      </div>
    </div>
  );
}
